import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Brush,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import Button from 'react-bootstrap/Button';
import ChartContext from './DataEntrySection/DataEntryProvider';
import { getSigDigitsUtil, formatChartPointDateTime, withPopupDateTime } from '../util/util';
import { buildSeriesLocations } from '../util/seriesPicker';
import { getLaboratoryOnly } from '../util/locationLabel';
import SeriesPickerModal from './Modal/SeriesPickerModal';

// Generate distinct, repeatable HSL-based colors
function getDistinctColor(index) {
  const goldenAngle = 137.508;
  const hue = (index * goldenAngle) % 360;
  return `hsl(${hue}, 70%, 50%)`;
}

function SQCAnalystChart({
  analysts,
  analystsDots,
  data,
  lines,
  syncId,
  xAxis,
  yMin,
  yMax,
  hideRegionArea = false,
  showDateTimeInTooltip = false,
  tz,
  dateTimeFormat
}) {
  const formatXAxis = (tickItem) => {
    return String(data[tickItem]?.date) || '';
  };

  const { currentControlStrategyConfigurations } = useContext(ChartContext);

  const [analystsArray, setAnalystsArray] = useState([]);
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    const initializedAnalysts = (analysts || []).map((analyst, index) => ({
      ...analyst,
      color: getDistinctColor(index),
      checked: analyst.checked ?? true
    }));
    setAnalystsArray(initializedAnalysts);
  }, [analysts]);

  const formatNothing = () => {
    return '';
  };

  const renderSigDigits = (stringValue) => {
    return getSigDigitsUtil(stringValue, currentControlStrategyConfigurations.chartSigDigits);
  };

  const formatYAxis = (tickItem) => {
    return renderSigDigits(tickItem);
  };

  // Shared by single toggles and Select all / Clear in the picker. Only the dots react;
  // the Data line itself always renders.
  const setAnalystsChecked = (names, checked) => {
    const nameSet = new Set(names);
    setAnalystsArray((prev) =>
      prev.map((analyst) => (nameSet.has(analyst.name) ? { ...analyst, checked } : analyst))
    );
  };

  const handleCheckboxClick = (name, checked) => setAnalystsChecked([name], checked);

  const selectedCount = analystsArray.filter((analyst) => analyst.checked).length;

  const analystLocations = useMemo(
    () => buildSeriesLocations(data, 'personnel', hideRegionArea ? getLaboratoryOnly : undefined),
    [data, hideRegionArea]
  );
  const pickerItems = analystsArray.map((analyst) => ({
    ...analyst,
    locations: analystLocations.get(analyst.name) || []
  }));

  const CustomizedDot = ({ cx, cy, payload, dataKey }) => {
    if (dataKey === 'resultOmits') {
      const currentAnalyst = analystsDots.find((analyst) => analyst.analyst === payload.personnel);
      const currentIndex = analystsArray.findIndex(
        (analyst) => analyst.name === currentAnalyst?.analyst
      );

      if (analystsArray[currentIndex]?.checked) {
        return (
          <svg
            x={cx - 3}
            y={cy - 3}
            width={15}
            height={15}
            key={`dot-${currentAnalyst?.analyst}-${cx}-${cy}`}
            fill={analystsArray[currentIndex].color}
            viewBox="0 0 1024 1024"
          >
            <path d="M512 256C512 397.4 397.4 512 256 512C114.6 512 0 397.4 0 256C0 114.6 114.6 0 256 0C397.4 0 512 114.6 512 256z" />
          </svg>
        );
      }
    }
    return null;
  };

  return (
    <>
      <div className="series-picker-toggle">
        <Button variant="outline-secondary" size="sm" onClick={() => setShowPicker(true)}>
          {`Analysts (${selectedCount} of ${analystsArray.length}) \u25BE`}
        </Button>
      </div>
      <SeriesPickerModal
        show={showPicker}
        onHide={() => setShowPicker(false)}
        title="Analysts"
        searchPlaceholder="Search analysts or locations"
        idPrefix="analyst-picker"
        items={pickerItems}
        onToggle={handleCheckboxClick}
        onSetChecked={setAnalystsChecked}
      />

      <ResponsiveContainer key={`SQCAnalystChart-${syncId}`} height={400}>
        <LineChart data={data} syncId={syncId} margin={{ top: 5, right: 20, bottom: 15, left: 20 }}>
          {lines
            ?.filter((l) => !l.reference)
            ?.map((line, index) => (
              <Line
                type="linear"
                key={line.yAxis || line.name}
                dataKey={line.yAxis}
                isAnimationActive={false}
                stroke={line.color}
                name={line.name}
                dot={
                  <CustomizedDot
                    key={`dot-${line.yAxis || line.name}-${index}`}
                    analystsDots={analystsDots}
                  />
                }
              />
            ))}

          {lines
            ?.filter((l) => l.reference)
            ?.map((line) => (
              <ReferenceLine
                y={line.overrideValue || (data[0] && data[0][line.yAxis])}
                key={line.yAxis || line.name}
                stroke={line.color}
                label={line.label}
              />
            ))}

          <CartesianGrid stroke="#ccc" key="cartesian-grid" strokeDasharray="5 5" />
          <XAxis dataKey={xAxis} key="x-axis" tickFormatter={formatXAxis} />
          <YAxis type="number" key="y-axis" domain={[yMin, yMax]} tickFormatter={formatYAxis} />
          <Brush dataKey={xAxis} key="brush" ticketFormatter={formatNothing} />
          <Legend verticalAlign="top" key="legend" height={36} />
          {showDateTimeInTooltip ? (
            <Tooltip
              key="tooltip"
              formatter={(value) => `${value}`}
              labelFormatter={(label) => {
                const pointDateTime = formatChartPointDateTime(data?.[label], tz, dateTimeFormat);
                return withPopupDateTime(label, pointDateTime);
              }}
            />
          ) : (
            <Tooltip key="tooltip" formatter={(value) => `${value}`} />
          )}
        </LineChart>
      </ResponsiveContainer>
    </>
  );
}

SQCAnalystChart.propTypes = {
  analysts: PropTypes.arrayOf(PropTypes.object).isRequired,
  analystsDots: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  lines: PropTypes.arrayOf(PropTypes.object).isRequired,
  xAxis: PropTypes.string.isRequired,
  syncId: PropTypes.string.isRequired,
  yMin: PropTypes.number.isRequired,
  yMax: PropTypes.number.isRequired,
  hideRegionArea: PropTypes.bool,
  showDateTimeInTooltip: PropTypes.bool,
  tz: PropTypes.string,
  dateTimeFormat: PropTypes.string
};

export default SQCAnalystChart;
